/* eslint-disable @typescript-eslint/no-restricted-imports */
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { type Control, Controller, useFieldArray } from 'react-hook-form';

import FormLabel from '~/common/components/Forms/FormLabel';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import Input from '~/common/components/Inputs/Input';
import Stack from '~/common/components/Stacks/Stack';

type UnitsFieldArrayProps = Readonly<{
  control: Control<Record<string, unknown>>;
  numberOfUnits: number | null;
  isWriteAccessDisabled?: boolean;
}>;

export default function UnitsFieldArray({
  control,
  numberOfUnits,
  isWriteAccessDisabled = false,
}: UnitsFieldArrayProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'units',
  });

  useEffect(() => {
    const count = Math.min(Number(numberOfUnits) || 0, 4);
    const diff = count - fields.length;

    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        append({ numberOfBedrooms: '', grossMonthlyRents: '' });
      }
    } else if (diff < 0) {
      for (let i = fields.length - 1; i >= count; i--) {
        remove(i);
      }
    }
  }, [fields.length, numberOfUnits, append, remove]);

  if (!fields.length) return null;

  return (
    <Stack
      sx={{
        width: '100%',
        marginBottom: '1rem',
        gap: '2rem',
      }}
    >
      {fields.map((field, index) => (
        <Stack key={field.id} sx={{ gap: '1rem' }}>
          <Typography variant="h3">{`Unit ${index + 1}`}</Typography>
          <Stack sx={{ flexDirection: 'row', width: '100%', gap: '2rem' }}>
            <Stack>
              <FormLabel htmlFor={`units.${index}.numberOfBedrooms`}>
                Number of Bedrooms
              </FormLabel>
              <Controller
                name={`units.${index}.numberOfBedrooms`}
                control={control}
                defaultValue={field.numberOfBedrooms ?? ''}
                render={({ value, onChange, onBlur }) => (
                  <Input
                    type="number"
                    id={`units.${index}.numberOfBedrooms`}
                    data-testid={`units-${index}-numberOfBedrooms`}
                    placeholder="Bedrooms"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isDisabled={isWriteAccessDisabled}
                    style={{ backgroundColor: 'white', width: '100%' }}
                  />
                )}
              />
            </Stack>
            <Stack>
              <FormLabel htmlFor={`units.${index}.grossMonthlyRents`}>
                Gross Monthly Rent
              </FormLabel>
              <Controller
                name={`units.${index}.grossMonthlyRents`}
                control={control}
                defaultValue={field.grossMonthlyRents ?? ''}
                render={({ value, onChange, onBlur }) => (
                  <CurrencyInput
                    id={`units.${index}.grossMonthlyRents`}
                    data-testid={`units-${index}-grossMonthlyRents`}
                    placeholder="Gross Monthly Rent"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isDisabled={isWriteAccessDisabled}
                    sx={{ backgroundColor: 'white', width: '100%' }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
